import { FC, useMemo, useState } from "react";
import { Stack, Tooltip, Typography } from "@mui/material";
import { formatCompactNumber, formatNumber } from "../../../../../../../../common/utils";
import { ChartNoDataOverlay } from "../../../../../../../../components/charts/components/ChartNoDataOverlay";
import { GraphBarChartLegend } from "../../../../../../../../components/charts/components/GraphBarChartLegend";
import { EnergyChartItem } from "../ChangeEmissionTab/data/mappersVehiclesFuels";

type Scenario = "baseline" | "comparison";

type BubblePoint = {
	id: string;
	label: string;
	scenario: Scenario;
	x: number;
	y: number;
	size: number;
	xMeasure: string;
	yMeasure: string;
	sizeMeasure: string;
};

type Props = {
	title: string;
	data?: EnergyChartItem[];
	baselineLabel?: string;
	comparisonLabel?: string;
	xAxisTitle?: string;
	yAxisTitle?: string;
};

const ticksX = [0, 20, 40, 60, 80, 100];

const chartPaddingX = 2;
const minBubbleSize = 28;
const maxBubbleSize = 72;

const EnergyIntensityBubbleChart: FC<Props> = ({
	title,
	data,
	baselineLabel = "Baseline",
	comparisonLabel = "Comparison",
	xAxisTitle = "tkm %",
	yAxisTitle = "KgCO2e / MJ",
}) => {
	const [visibleScenarios, setVisibleScenarios] = useState<Record<Scenario, boolean>>({
		baseline: true,
		comparison: true,
	});

	// Builds one baseline and comparison point per item.
	const points = useMemo<BubblePoint[]>(
		() =>
			(data ?? []).flatMap((item) => [
				{
					id: `${item.id}-baseline`,
					label: item.label,
					scenario: "baseline",
					x: item.baseline.share.value,
					y: item.baseline.intensity.value,
					size: item.baseline.co2.value,
					xMeasure: item.baseline.share.measure,
					yMeasure: item.baseline.intensity.measure,
					sizeMeasure: item.baseline.co2.measure,
				},
				{
					id: `${item.id}-comparison`,
					label: item.label,
					scenario: "comparison",
					x: item.comparison.share.value,
					y: item.comparison.intensity.value,
					size: item.comparison.co2.value,
					xMeasure: item.comparison.share.measure,
					yMeasure: item.comparison.intensity.measure,
					sizeMeasure: item.comparison.co2.measure,
				},
			]),
		[data]
	);

	const hasNoData = points.length === 0;
	const visiblePoints = useMemo(
		() => points.filter((point) => visibleScenarios[point.scenario]),
		[points, visibleScenarios]
	);

	const toggleScenario = (scenario: Scenario) => {
		setVisibleScenarios((current) => ({
			...current,
			[scenario]: !current[scenario],
		}));
	};

	// Uses the largest Y value to scale the vertical axis.
	const axisMaxY = useMemo(() => {
		const maxY = Math.max(...points.map((point) => point.y), 0.01);

		return Math.ceil(maxY * 100) / 100;
	}, [points]);

	// Uses the largest size value to scale the bubbles.
	const maxSize = useMemo(() => Math.max(...points.map((point) => Math.abs(point.size)), 1), [points]);

	// Keeps the Y axis simple and readable.
	const ticksY = useMemo(() => [0, axisMaxY / 2, axisMaxY], [axisMaxY]);

	// Converts the size value into a bubble diameter.
	const getBubbleSize = (value: number): number => {
		const ratio = Math.abs(value) / maxSize;

		return minBubbleSize + ratio * (maxBubbleSize - minBubbleSize);
	};

	const formatPercent = (value: number): string => `${formatNumber(value, "decimal", 1, 1)}%`;

	const formatIntensity = (value: number): string => formatNumber(value, "decimal", 2, 2);

	return (
		<Stack gap={2} width="100%" mb={2}>
			<Typography variant="subtitle1" fontWeight={600}>
				{title}
			</Typography>

			{/* Chart */}
			<Stack direction="row" minHeight={260} pr={2} pt={4}>
				{/* Y axis title */}
				<Stack
					width={24}
					alignItems="center"
					justifyContent="center"
					sx={{
						writingMode: "vertical-rl",
						transform: "rotate(180deg)",
					}}
				>
					<Typography variant="caption" fontWeight={600} whiteSpace="nowrap">
						{yAxisTitle}
					</Typography>
				</Stack>

				{/* Y axis labels */}
				<Stack width={48} justifyContent="space-between" alignItems="end" pr={1}>
					{[...ticksY].reverse().map((tick) => (
						<Typography key={tick} variant="caption" color="text.secondary">
							{formatIntensity(tick)}
						</Typography>
					))}
				</Stack>

				{/* Chart area */}
				<Stack flex={1} position="relative" px={chartPaddingX}>
					{/* Vertical grid lines */}
					{ticksX.map((tick) => (
						<Stack
							key={tick}
							position="absolute"
							left={`${tick}%`}
							top={0}
							bottom={0}
							width="1px"
							bgcolor="divider"
							sx={{
								transform: "translateX(-50%)",
								opacity: 0.75,
							}}
						/>
					))}

					{/* Empty state */}
					{hasNoData && <ChartNoDataOverlay />}

					{/* Bubbles */}
					{!hasNoData &&
						visiblePoints.map((point) => {
							const diameter = getBubbleSize(point.size);

							const scenarioLabel = point.scenario === "baseline" ? baselineLabel : comparisonLabel;

							return (
								<Tooltip
									key={point.id}
									arrow
									placement="right"
									slotProps={{
										tooltip: {
											sx: {
												bgcolor: "background.paper",
												color: "text.primary",
												boxShadow: 3,
												borderRadius: 1,
												p: 0,
												maxWidth: 320,
											},
										},
										arrow: {
											sx: {
												color: "background.paper",
											},
										},
									}}
									title={
										<Stack p={1} minWidth={230}>
											<Typography variant="caption" fontWeight={600}>
												{scenarioLabel} ({point.label})
											</Typography>

											<Stack direction="row" justifyContent="space-between" gap={1}>
												<Typography variant="caption">tkm_share_by_mode</Typography>

												<Typography variant="caption">{formatPercent(point.x)}</Typography>
											</Stack>

											<Stack direction="row" justifyContent="space-between" gap={1}>
												<Typography variant="caption">
													{point.yMeasure === "MJ/tkm" ? "mj_tkm" : "co2_mj"}
												</Typography>

												<Typography variant="caption">{formatIntensity(point.y)}</Typography>
											</Stack>

											<Stack direction="row" justifyContent="space-between" gap={1}>
												<Typography variant="caption">co2_tot</Typography>

												<Typography variant="caption">
													{formatCompactNumber(point.size, 0, 1)} {point.sizeMeasure}
												</Typography>
											</Stack>
										</Stack>
									}
								>
									<Stack
										position="absolute"
										left={`${point.x}%`}
										bottom={`${(point.y / axisMaxY) * 100}%`}
										width={diameter}
										height={diameter}
										borderRadius="50%"
										bgcolor={point.scenario === "baseline" ? "primary.main" : "secondary.main"}
										sx={{
											transform: "translate(-50%, 50%)",
											opacity: 0.82,
										}}
									/>
								</Tooltip>
							);
						})}
				</Stack>
			</Stack>

			{/* X axis labels */}
			<Stack direction="row" pr={2}>
				<Stack width={72} />

				<Stack flex={1} position="relative" px={chartPaddingX} height={22}>
					{ticksX.map((tick) => (
						<Typography
							key={tick}
							variant="caption"
							color="text.secondary"
							position="absolute"
							left={`${tick}%`}
							sx={{
								transform: "translateX(-50%)",
							}}
						>
							{tick}%
						</Typography>
					))}
				</Stack>
			</Stack>

			{/* X axis title */}
			<Stack direction="row" pr={2}>
				<Stack width={72} />

				<Stack flex={1} alignItems="center">
					<Typography variant="caption" fontWeight={600}>
						{xAxisTitle}
					</Typography>
				</Stack>
			</Stack>

			{/* Legend */}
			{!hasNoData && (
				<GraphBarChartLegend
					primaryLable={baselineLabel}
					primaryColor="primary.main"
					secondaryColor="secondary.main"
					secondaryLable={comparisonLabel}
					primaryActive={visibleScenarios.baseline}
					secondaryActive={visibleScenarios.comparison}
					onPrimaryClick={() => toggleScenario("baseline")}
					onSecondaryClick={() => toggleScenario("comparison")}
				/>
			)}
		</Stack>
	);
};

export default EnergyIntensityBubbleChart;
